/**
 * Chord FIB — fill in the blank.
 *
 * One quality plus one known degree determines every other note, because a quality
 * fixes every interval inside the chord. That solver is the feature; the two tabs
 * are just two ways to use it.
 *
 *   Cheat / Chord Builder — say what you know, get the chord built and explained.
 *   Practice             — get a challenge, write it by hand, be graded.
 *
 * The picker offers twelve pitch classes and never shows an octave. Register and
 * spelling are decided for the learner (see theory/voicing.ts): the app chooses the
 * enharmonic that makes the chord spell in thirds, and a voicing centred on the
 * chosen clef.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { WritingPad, type WritingPadHandle } from '../components/WritingPad';
import { Score } from '../components/Score';
import { PitchClassPicker } from '../components/PitchClassPicker';
import { GuitarView } from '../components/GuitarView';
import type { RecognitionResult } from '../ink/recognize';
import { CLEFS, CLEF_INFO, type Clef } from '../theory/staff';
import { QUALITIES, degreeLabel, type ChordQuality } from '../theory/chords';
import {
  buildChordFromDegree,
  pitchClassName,
  type AccidentalPreference,
} from '../theory/voicing';
import { isAwkwardSpelling, pitchClass, pitchName, type Pitch } from '../theory/pitch';
import type { BuildRequest } from '../auth/routes';
import { Button } from '../design/Button';
import { SegmentedControl } from '../design/Navigation';
import { playPitches, stop } from '../audio/player';

const FAMILY_LABELS: Record<ChordQuality['family'], string> = {
  triad: 'Triads',
  seventh: 'Sevenths',
  sixth: 'Sixths',
  extended: 'Extensions',
  altered: 'Altered',
};

type Mode = 'choose' | 'cheat' | 'practice';

/**
 * The twelve pitch classes, as roots.
 *
 * Spelling follows the learner's sharp/flat preference, exactly as the existing
 * degree picker does — the brief is explicit that this control must not
 * introduce a second way of naming the same note, and no octave numbers appear
 * anywhere in the UI.
 */
const ROOT_CLASSES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

/** Inversions the pilot supports: which chord tone is in the bass. */
const INVERSIONS = [
  { value: 0, label: 'Root position' },
  { value: 1, label: '1st inversion' },
  { value: 2, label: '2nd inversion' },
  { value: 3, label: '3rd inversion' },
];

interface Grade {
  correct: Pitch[];
  missing: Pitch[];
  extra: Pitch[];
  passed: boolean;
}

function sameSpelling(a: Pitch, b: Pitch): boolean {
  return a.letter === b.letter && a.alter === b.alter;
}

export interface ChordFIBProps {
  /**
   * A chord the Chord Legend asked for, read from the URL.
   *
   * Applied once, as the screen's initial state, rather than pushed in by an
   * effect: an effect that kept re-applying it would fight the learner every
   * time they changed the root, and Back would land them on a screen that
   * immediately reset itself.
   */
  build?: BuildRequest;
  /** Open the Chord Legend. */
  onOpenLegend?: () => void;
  /**
   * Report the current selection so it can be written into the URL.
   *
   * This screen is unmounted the moment the learner opens the Chord Legend —
   * routes replace the whole surface — so nothing held in component state comes
   * back when they press Back. An earlier comment here claimed React preserved
   * it; it does not, and a learner who had set up B♭m7 found Cmaj7 waiting for
   * them. The address bar is the only thing that survives an unmount, a
   * refresh, and the back button, so the selection is kept there.
   */
  onStateChange?: (build: BuildRequest) => void;
}

export function ChordFIB({ build, onOpenLegend, onStateChange }: ChordFIBProps = {}) {
  const padRef = useRef<WritingPadHandle>(null);

  /*
   * A request from the Legend opens the chooser, because that is the mode that
   * answers "here is a chord, show it to me". Without a request the builder
   * stays the default — changing what the screen opens on would be a change
   * nobody asked for.
   */
  const requested = build && QUALITIES.some((q) => q.id === build.qualityId) ? build : undefined;
  const [mode, setMode] = useState<Mode>(requested?.mode ?? (requested ? 'choose' : 'cheat'));
  /** Root as a pitch class, for the direct chooser. */
  const [rootClass, setRootClass] = useState(requested?.rootClass ?? 0);
  const [inversion, setInversion] = useState(requested?.inversion ?? 0);
  const [clef, setClef] = useState<Clef>((requested?.clef as Clef | undefined) ?? 'treble');
  const [qualityId, setQualityId] = useState(requested?.qualityId ?? 'maj7');
  const [slotIndex, setSlotIndex] = useState(3);
  const [knownClass, setKnownClass] = useState(11); // B, the 7th of Cmaj7
  const [preference, setPreference] = useState<AccidentalPreference>(
    requested?.spelling ?? 'flat',
  );

  const [built, setBuilt] = useState(true);
  const [showGuitar, setShowGuitar] = useState(false);
  const [grade, setGrade] = useState<Grade | null>(null);
  const [written, setWritten] = useState<RecognitionResult | null>(null);
  const [revealed, setRevealed] = useState(false);

  const quality = useMemo(
    () => QUALITIES.find((q) => q.id === qualityId) ?? QUALITIES[0],
    [qualityId],
  );

  /**
   * Keep the address bar describing what is on screen.
   *
   * Replaced rather than pushed: changing the root is not a new place, it is the
   * same exercise adjusted, and pushing would make Back walk through every
   * adjustment before leaving the screen. Replacing means the entry the learner
   * returns to already holds their selection.
   */
  const reportState = useRef(onStateChange);
  reportState.current = onStateChange;
  useEffect(() => {
    reportState.current?.({
      rootClass,
      qualityId,
      spelling: preference,
      mode,
      clef,
      inversion,
    });
  }, [rootClass, qualityId, preference, mode, clef, inversion]);

  // Choosing a chord by name is the same question the builder already answers:
  // the root is simply degree 0. Routing it through `buildChordFromDegree`
  // rather than adding a second code path is what keeps the two intentions from
  // ever disagreeing about what Cmaj7 is.
  const effectiveSlot = mode === 'choose' ? 0 : slotIndex;
  const effectiveKnown = mode === 'choose' ? rootClass : knownClass;
  const tone = quality.tones[Math.min(effectiveSlot, quality.tones.length - 1)];

  const solved = useMemo(
    () => buildChordFromDegree(effectiveKnown, quality, tone, clef, preference),
    [effectiveKnown, quality, tone, clef, preference],
  );

  /**
   * The voicing as displayed and played.
   *
   * An inversion moves a tone to the bass; it does not change the chord. The
   * pitch classes are identical, which is why grading and the chord name are
   * unaffected by it.
   */
  const voiced = useMemo(() => {
    if (mode !== 'choose' || inversion === 0) return solved.voicing;
    const rotations = Math.min(inversion, solved.voicing.length - 1);
    const notes = solved.voicing.map((note) => ({ ...note }));
    for (let step = 0; step < rotations; step += 1) {
      const lowest = notes.shift();
      if (lowest) notes.push({ ...lowest, octave: lowest.octave + 1 });
    }
    return notes;
  }, [mode, inversion, solved.voicing]);

  const bassName = pitchName(voiced[0]).replace(/\d/g, '');
  const rootName = pitchName(solved.root).replace(/\d/g, '');
  const chordName =
    mode === 'choose' && inversion > 0 && bassName !== rootName
      ? `${pitchName(solved.root)}${quality.symbol}/${bassName}`
      : `${pitchName(solved.root)}${quality.symbol}`;
  const awkward = solved.voicing.filter(isAwkwardSpelling);

  // Keep the chosen degree valid when the quality changes.
  useEffect(() => {
    setSlotIndex((i) => Math.min(i, quality.tones.length - 1));
    // Third inversion exists only when the chord has a seventh. Keep the option
    // visible as a theory lesson, but never carry an invalid bass into a triad.
    setInversion((value) => Math.min(value, quality.tones.length - 1));
    setGrade(null);
    setRevealed(false);
  }, [quality]);

  const newChallenge = useCallback(() => {
    const pool = QUALITIES.filter((q) => q.family === 'triad' || q.family === 'seventh');
    const nextQuality = pool[Math.floor(Math.random() * pool.length)];
    setQualityId(nextQuality.id);
    setSlotIndex(Math.floor(Math.random() * nextQuality.tones.length));
    setKnownClass(Math.floor(Math.random() * 12));
    setGrade(null);
    setRevealed(false);
    setBuilt(true);
    padRef.current?.clear();
  }, []);

  const check = useCallback(() => {
    const result = padRef.current?.recognizeNow() ?? written;
    const target = solved.voicing;
    if (!result || !result.notes.length) {
      setGrade({ correct: [], missing: target, extra: [], passed: false });
      return;
    }
    // Graded by spelling, ignoring octave: Cmaj7 an octave low is still Cmaj7,
    // but B and C♭ are not interchangeable.
    const writtenPitches = result.notes.map((n) => n.pitch);
    const correct: Pitch[] = [];
    const missing: Pitch[] = [];
    const used = new Set<number>();

    for (const wanted of target) {
      const index = writtenPitches.findIndex((p, i) => !used.has(i) && sameSpelling(p, wanted));
      if (index >= 0) {
        used.add(index);
        correct.push(wanted);
      } else {
        missing.push(wanted);
      }
    }
    const extra = writtenPitches.filter((_, i) => !used.has(i));
    setGrade({ correct, missing, extra, passed: missing.length === 0 && extra.length === 0 });
  }, [solved.voicing, written]);

  return (
    <div className="feature">
      <header className="feature-head">
        <div>
          <p className="eyebrow">Chord FIB</p>
          <h2>Fill in one blank. The chord builds itself.</h2>
          <p className="lede">
            A quality fixes every interval inside the chord, so one known note fixes all the
            others. Tell it the 7th is B in a major seventh chord and there is exactly one
            answer: C major seventh.
          </p>
        </div>

        {/* Back to the reference without losing what is on this screen: the
            Legend is a different route, and FIB's own state stays where it is. */}
        {onOpenLegend ? (
          <Button variant="tertiary" onClick={onOpenLegend}>
            Chord Legend
          </Button>
        ) : null}
      </header>

      {/* Three distinct intentions. Pick the workflow before presenting its
          controls, so the learner never sees the chooser and the one-note
          solver stacked together as if they were one long form. */}
      <SegmentedControl
        label="Chord FIB mode"
        value={mode}
        onChange={setMode}
        options={[
          { value: 'choose', label: 'Choose a chord' },
          { value: 'cheat', label: 'Chord builder' },
          { value: 'practice', label: 'Practice' },
        ]}
      />

      {mode === 'choose' ? (
        <section className="fib__chooser" aria-label="Choose a chord">
          <p className="fib__chooser-note">
            Name the chord you want. The three intentions are deliberately separate: this one
            builds a chord you already have in mind, the builder infers it from one note you know,
            and Practice hides tones for you to write.
          </p>

          <div className="fib__chooser-row">
            <div
              className="fib__roots"
              role="radiogroup"
              aria-label="Root"
            >
              {ROOT_CLASSES.map((pitchClass) => (
                <button
                  key={pitchClass}
                  type="button"
                  role="radio"
                  aria-checked={pitchClass === rootClass}
                  className={`fib__root ${pitchClass === rootClass ? 'is-selected' : ''}`}
                  onClick={() => {
                    setRootClass(pitchClass);
                    setBuilt(false);
                  }}
                >
                  {/* No octave numbers anywhere, per the brief. */}
                  {pitchClassName(pitchClass, preference)}
                </button>
              ))}
            </div>
          </div>

          <div className="fib__chooser-row">
            <label className="fib__field">
              <span>Quality</span>
              <select
                aria-label="Quality"
                value={qualityId}
                onChange={(event) => {
                  setQualityId(event.target.value);
                  setBuilt(false);
                }}
              >
                {Object.entries(FAMILY_LABELS).map(([family, label]) => (
                  <optgroup key={family} label={label}>
                    {QUALITIES.filter((quality) => quality.family === family).map((quality) => (
                      <option key={quality.id} value={quality.id}>
                        {quality.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>

            <label className="fib__field">
              <span>Inversion</span>
              <select
                aria-label="Inversion"
                value={inversion}
                onChange={(event) => {
                  setInversion(Number(event.target.value));
                  setBuilt(false);
                }}
              >
                {INVERSIONS.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                    disabled={option.value >= quality.tones.length}
                  >
                    {option.label}{option.value === 3 ? ' · seventh chords' : ''}
                  </option>
                ))}
              </select>
              <small className="fib__field-hint">
                3rd inversion puts the chord’s 7th in the bass.
              </small>
            </label>
          </div>

          <Button variant="primary" onClick={() => setBuilt(true)}>
            Build chord
          </Button>
        </section>
      ) : null}

      {/* ---------------------------------------------------------- inputs */}
      {mode !== 'choose' ? <>
      <div className="fib-controls fib-controls--solver">
        <label className="field">
          <span>Chord quality</span>
          <select
            value={qualityId}
            onChange={(e) => {
              setQualityId(e.target.value);
              setBuilt(mode === 'practice');
            }}
          >
            {(['triad', 'seventh', 'sixth', 'extended', 'altered'] as const).map((family) => (
              <optgroup key={family} label={FAMILY_LABELS[family]}>
                {QUALITIES.filter((q) => q.family === family).map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.name} ({q.symbol || 'major'})
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>

        <label className="field">
          <span>Known degree</span>
          <select
            value={slotIndex}
            onChange={(e) => {
              setSlotIndex(Number(e.target.value));
              setBuilt(mode === 'practice');
            }}
            aria-label="Which degree do you know"
          >
            {quality.tones.map((t, i) => (
              <option key={degreeLabel(t)} value={i}>
                the {degreeLabel(t)}
              </option>
            ))}
          </select>
        </label>

        <SegmentedControl
          label="Clef"
          value={clef}
          onChange={setClef}
          options={CLEFS.map((c) => ({ value: c, label: CLEF_INFO[c].label }))}
        />
      </div>

      <PitchClassPicker
        label={`The ${degreeLabel(tone)} is…`}
        value={knownClass}
        onChange={(pc) => {
          setKnownClass(pc);
          setBuilt(mode === 'practice');
          setGrade(null);
        }}
        preference={preference}
        onPreferenceChange={setPreference}
      />

      {mode === 'cheat' && !built ? (
        <Button variant="primary" size="lg" block onClick={() => setBuilt(true)}>
          Build chord
        </Button>
      ) : null}
      </> : null}

      {/* ---------------------------------------------------------- result */}
      {built ? (
        <>
          <div className="fib-slots" role="group" aria-label="Chord degrees">
            {quality.tones.map((t, i) => (
              <div key={degreeLabel(t)} className="slot-wrap">
                <button
                  type="button"
                  className={`slot ${i === slotIndex ? 'is-given' : 'is-derived'}`}
                  aria-pressed={i === slotIndex}
                  onClick={() => {
                    // Re-anchor on this degree, keeping the note that is already there.
                    setKnownClass(pitchClass(solved.voicing[i]));
                    setSlotIndex(i);
                    setGrade(null);
                  }}
                >
                  <span className="slot-degree">{degreeLabel(t)}</span>
                  <span className="slot-note">{pitchName(solved.voicing[i])}</span>
                  <span className="slot-tag">{i === slotIndex ? 'you filled this' : 'derived'}</span>
                </button>
              </div>
            ))}
          </div>

          <div className="answer-card">
            <div className="answer-head">
              <div>
                <p className="eyebrow">Detected chord</p>
                <p className="chord-name big">{chordName}</p>
                <p className="chord-sub">
                  {quality.name} · root {pitchName(solved.root)} ·{' '}
                  {voiced.map(pitchName).join(' – ')}
                </p>
              </div>
              <div className="play-row">
                <Button variant="primary" icon="play" onClick={() => playPitches(voiced, 'both')}>
                  Listen
                </Button>
                <Button variant="tertiary" icon="stop" onClick={stop}>
                  Stop
                </Button>
                <Button
                  variant="secondary"
                  aria-pressed={showGuitar}
                  onClick={() => setShowGuitar((v) => !v)}
                >
                  {showGuitar ? 'Back to notation' : 'Flip to Guitar / TAB'}
                </Button>
              </div>
            </div>

            {showGuitar ? (
              <GuitarView chord={voiced} quality={quality} chordName={chordName} />
            ) : (
              <Score clef={clef} groups={[voiced]} labels={[chordName]} height={190} />
            )}

            <p className="cause">{quality.cause}</p>

            {solved.theoretical ? (
              <p className="caveat">
                Solving from that note gives a root that needs more than a double accidental. It
                exists on paper, but nobody writes it — pick a different pitch class for this
                degree and you will land on a real key.
              </p>
            ) : null}

            {awkward.length && !solved.theoretical ? (
              <p className="caveat">
                Spelled strictly in thirds this chord contains {awkward.map(pitchName).join(', ')}.
                That is correct, not a bug — the letters have to keep stacking, even when the
                result looks unusual.
              </p>
            ) : null}
          </div>
        </>
      ) : null}

      {/* -------------------------------------------------------- practice */}
      {mode === 'practice' ? (
        <section className="practice">
          <div className="practice-head">
            <div>
              <h3>Now write it out</h3>
              <p className="lede">
                Write {chordName} on the {CLEF_INFO[clef].label.toLowerCase()} staff and check
                yourself.
              </p>
            </div>
            <div className="practice-actions">
              <Button variant="primary" onClick={check}>
                Check my writing
              </Button>
              <Button variant="secondary" icon="redo" onClick={newChallenge}>
                New challenge
              </Button>
              <Button
                variant="tertiary"
                onClick={() => {
                  padRef.current?.clear();
                  setGrade(null);
                }}
              >
                Clear
              </Button>
              <Button variant="tertiary" onClick={() => setRevealed((v) => !v)}>
                {revealed ? 'Hide answer' : 'Show answer'}
              </Button>
            </div>
          </div>

          <WritingPad
            ref={padRef}
            clef={clef}
            onResult={setWritten}
            height={340}
            hint={`Stack ${solved.voicing.length} noteheads in a vertical line. Write any accidentals just to the left of their notehead.`}
          />

          {revealed ? (
            <div className="reveal">
              <Score clef={clef} groups={[solved.voicing]} height={170} color="#d4a74f" />
            </div>
          ) : null}

          {grade ? (
            <div className={`grade ${grade.passed ? 'is-pass' : 'is-fail'}`}>
              <p className="grade-head">
                {grade.passed
                  ? `That is ${chordName}. All ${solved.voicing.length} notes correct.`
                  : `Not ${chordName} yet.`}
              </p>
              {grade.correct.length ? (
                <p>
                  <strong>Right:</strong> {grade.correct.map(pitchName).join(', ')}
                </p>
              ) : null}
              {grade.missing.length ? (
                <p>
                  <strong>Missing:</strong> {grade.missing.map(pitchName).join(', ')} — check the
                  interval above the root for each one.
                </p>
              ) : null}
              {grade.extra.length ? (
                <p>
                  <strong>Not in this chord:</strong> {grade.extra.map(pitchName).join(', ')}. If
                  you meant one of these, the recogniser may have snapped a notehead to the wrong
                  line — write a little larger and centre it on the line or space.
                </p>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : mode === 'cheat' ? (
        <p className="fine">
          Chose the {degreeLabel(tone)} as {pitchClassName(knownClass, preference)}. Switch to
          Practice to write this chord by hand and be graded on it.
        </p>
      ) : null}
    </div>
  );
}
